import { useCallback, useEffect, useState } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage } from '../../utils/apiMessages';
import { useCityInView } from '../city/useHubCity';

/**
 * What the shops of the neighbourhood are offering today.
 *
 * Only what is running: an offer that ended yesterday stops being returned by
 * the API, so there is nothing to filter here and nothing to expire.
 */
export function useOffers({ neighborhood = '', category = '', provider = '', limit } = {}) {
  const { cityParam } = useCityInView();
  const [offers, setOffers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Bumped after a moderator hides one, so the list reloads in place.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const params = new URLSearchParams();
        if (neighborhood) params.set('neighborhood', neighborhood);
        if (category) params.set('category', category);
        if (provider) params.set('provider', provider);
        if (limit) params.set('limit', String(limit));
        if (cityParam) params.set('city', cityParam);

        const response = await apiCall(`/offers?${params.toString()}`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (cancelled) return;

        if (!response.ok) {
          throw new Error(translateApiMessage(data.error) || 'Les offres n’ont pas pu être chargées.');
        }

        setOffers(data.data || []);
        setMeta(data.meta || null);
        setError('');
      } catch (err) {
        if (!cancelled) {
          setOffers([]);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [neighborhood, category, provider, limit, cityParam, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { offers, meta, loading, error, reload };
}
